import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { fmtDateTime } from "@/lib/date";
import VettingSection from "@/components/vetting/VettingSection";
import { PrimaryZone } from "@/components/vetting/ResourceCard";

export function AdminSync({ slug = "vetting", onDone }) {
  const [running, setRunning] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const poll = () => {
    setRunning(true);
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/${slug}/sync`);
        if (data.running) return;
        clearInterval(timer.current);
        setRunning(false);
        if (data.error) toast.error(`Sync failed — previous content kept. ${data.error}`);
        else { toast.success("Synced from the master sheet"); onDone(); }
      } catch {
        clearInterval(timer.current);
        setRunning(false);
        toast.error("Lost contact with the sync — refresh to check the latest sync time");
      }
    }, 4000);
  };
  const run = async () => {
    try {
      await api.post(`/admin/${slug}/sync`);
      poll();
    } catch (e) {
      if (e?.response?.status === 409) poll();
      else toast.error(e?.response?.data?.detail || "Couldn't start sync");
    }
  };
  return (
    <button onClick={run} disabled={running} data-testid={`${slug}-sync-btn`}
            className="inline-flex items-center gap-1 text-[11px] text-[#0A1F44] hover:text-[#8A6D1F] disabled:opacity-60">
      <RefreshCw className={`w-3 h-3 ${running ? "animate-spin" : ""}`} /> {running ? "Syncing (about 1–2 min)…" : "Sync from sheet"}
    </button>
  );
}

export function StageNav({ stages, current, base = "/vetting", chooseLabel = "Choose a stage", testid = "vetting" }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="lg:hidden">
        <label className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold" htmlFor={`${testid}-stage-select`}>{chooseLabel}</label>
        <Select value={current} onValueChange={(v) => navigate(`${base}/${v}`)}>
          <SelectTrigger id={`${testid}-stage-select`} className="mt-1 h-11 bg-white" data-testid={`${testid}-stage-select`}><SelectValue /></SelectTrigger>
          <SelectContent>{stages.map((s, i) => <SelectItem key={s.id} value={s.id}>{i}. {s.nav}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <nav className="hidden lg:block sticky top-24 self-start" aria-label="Stages" data-testid={`${testid}-stage-nav`}>
        <ol className="space-y-1">
          {stages.map((s, i) => (
            <li key={s.id}>
              <Link to={`${base}/${s.id}`} data-testid={`${testid}-nav-${s.id}`} aria-current={s.id === current ? "page" : undefined}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${s.id === current ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-white"}`}>
                <span className={`w-6 h-6 rounded-full text-[11px] flex items-center justify-center shrink-0 ${s.id === current ? "bg-[#C9A961] text-[#0A1F44]" : "bg-[#0A1F44]/5 text-[#0A1F44]"}`}>{i}</span>
                {s.nav}
              </Link>
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}

export default function Vetting() {
  const { stageId } = useParams();
  const { user, isViewAs } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/training/vetting").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the guide"));
  useEffect(() => { load(); }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [stageId]);
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading guide…</div>;

  const idx = Math.max(0, data.stages.findIndex((s) => s.id === stageId));
  const stage = data.stages[idx];
  const prev = data.stages[idx - 1];
  const next = data.stages[idx + 1];
  const [titleMain, ...titleSub] = (stage.name || "").split("\n");

  return (
    <div className="space-y-6" data-testid="vetting-page">
      <section className="rounded-2xl bg-[#0A1F44] text-white p-5 sm:p-7 ring-1 ring-[#C9A961]/30">
        <Link to="/hub" className="inline-flex items-center min-h-[44px] text-sm text-white/80 hover:text-white">← Resources</Link>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1">The Vetting Process</h1>
        <p className="text-white/70 text-sm mt-1 max-w-2xl">Learn how to guide someone through each step—from the first conversation to supporting a new partner.</p>
      </section>
      <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
        <StageNav stages={data.stages} current={stage.id} />
        <article className="space-y-4 min-w-0" data-testid={`vetting-stage-${stage.id}`}>
          <header>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold" data-testid="vetting-stage-code">
              Stage {idx} of {data.stages.length - 1} · {stage.id === "start-here" ? "Start Here" : stage.code}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0A1F44] mt-1" data-testid="vetting-stage-title">{titleMain}</h2>
            {titleSub.length > 0 && <p className="text-sm text-slate-500">{titleSub.join(" ")}</p>}
          </header>
          {stage.sections.map((sec, i) => <PrimaryZone key={`${stage.id}-${i}`} on={i < 2}><VettingSection sec={sec} testid={`vetting-sec-${sec.id}`} /></PrimaryZone>)}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
            {prev ? (
              <Button variant="outline" asChild className="rounded-full h-auto min-h-[44px] py-2 whitespace-normal text-left"><Link to={`/vetting/${prev.id}`} data-testid="vetting-prev"><ChevronLeft className="w-4 h-4 mr-1" />{prev.nav}</Link></Button>
            ) : <span />}
            {next && (
              <Button asChild className="rounded-full h-auto min-h-[44px] py-2 whitespace-normal text-left ml-auto bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"><Link to={`/vetting/${next.id}`} data-testid="vetting-next">{next.nav}<ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-600 pt-2" data-testid="vetting-synced-at">
            Content last synced from the master sheet {fmtDateTime(data.synced_at)} (Melbourne)
            {user?.can_sync_content && !isViewAs && <AdminSync onDone={load} />}
          </div>
        </article>
      </div>
    </div>
  );
}
